/**
 * The set of models answering a prompt.
 *
 * Capped at three: each channel is a full independent turn, so cost and latency both
 * scale linearly, and four columns stop being readable on a laptop.
 */
export const MAX_CHANNELS = 3;

export type Channel = { id: string; modelId: string };

export function canAddChannel(channels: Channel[]): boolean {
  return channels.length < MAX_CHANNELS;
}

/**
 * Adds a channel, preferring a model not already in play — adding a second column
 * that duplicates the first is never what someone comparing models wants.
 */
export function addChannel(channels: Channel[], available: { id: string }[]): Channel[] {
  if (!canAddChannel(channels)) return channels;
  const inUse = new Set(channels.map((c) => c.modelId));
  const next = available.find((m) => !inUse.has(m.id))?.id ?? channels[channels.length - 1]?.modelId;
  if (!next) return channels;
  return [...channels, { id: `ch-${Date.now()}-${channels.length}`, modelId: next }];
}

/** Removing the last channel is refused — the surface always has something to talk to. */
export function removeChannel(channels: Channel[], id: string): Channel[] {
  if (channels.length <= 1) return channels;
  return channels.filter((c) => c.id !== id);
}

export function setChannelModel(channels: Channel[], id: string, modelId: string): Channel[] {
  return channels.map((c) => (c.id === id ? { ...c, modelId } : c));
}
